import type { StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import InfoCircleIcon from '@/components/Icons/InfoCircleIcon'
import { FormField } from '@/components/MultiStepForm/FormField/FormField'
import MultiSelectRadix from '@/components/ui/MultiSelectRadix'
import { useIndustriesQuery } from '@/services/staticValues/queries'
import { Switch, Tooltip } from 'radix-ui'
import type React from 'react'
import styles from './InfluencersRequirements.module.css'

export const InfluencersRequirements = ({ data, updateData, errors }: StepProps) => {
  const { data: industries } = useIndustriesQuery()

  const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
      e.preventDefault()
    }
  }

  const handlePositiveNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'instagramFollowersCount' | 'youtubeFollowersCount' | 'tiktokFollowersCount',
  ) => {
    const val = e.target.value
    if (val === '') {
      updateData({ [field]: undefined })
    } else {
      const n = Math.max(1, Number(val))
      updateData({ [field]: n })
    }
  }

  return (
    <div className={styles.Container}>
      <FormField label="Industry" error={errors?.industries}>
        <MultiSelectRadix
          ariaLabel="Select industries"
          placeholder="Choose industries..."
          options={industries?.items.sort((a, b) => a.localeCompare(b)).map((industry) => industry) || []}
          value={data.industries || []}
          onChange={(value) => updateData({ industries: value })}
          maxSelected={3}
        />
      </FormField>

      <div
        className={styles.SwitchAndInput}
        style={errors?.instagramFollowersCount ? { alignItems: 'center' } : { alignItems: 'flex-end' }}
      >
        <Switch.Root
          className={styles.Root}
          id="instagram"
          checked={data.instagramFollowersRequired || false}
          onCheckedChange={(checked) => updateData({ instagramFollowersRequired: checked })}
        >
          <Switch.Thumb className={styles.Thumb} />
        </Switch.Root>

        <FormField label="Instagram" error={errors?.instagramFollowersCount}>
          <input
            disabled={!data.instagramFollowersRequired}
            placeholder="Type Min Followers Number"
            type="number"
            min={1}
            step={1}
            className={styles.Input}
            value={data.instagramFollowersCount || ''}
            onChange={(e) => handlePositiveNumberChange(e, 'instagramFollowersCount')}
            onKeyDown={handleNumberKeyDown}
          />
        </FormField>
      </div>
      <div className={styles.SwitchAndInput} style={errors?.youtubeFollowersCount ? { alignItems: 'center' } : { alignItems: 'flex-end' }}>
        <Switch.Root
          className={styles.Root}
          id="youtube"
          checked={data.youtubeFollowersRequired || false}
          onCheckedChange={(checked) => updateData({ youtubeFollowersRequired: checked })}
        >
          <Switch.Thumb className={styles.Thumb} />
        </Switch.Root>

        <FormField label="You Tube" error={errors?.youtubeFollowersCount}>
          <input
            disabled={!data.youtubeFollowersRequired}
            placeholder="Type Min Followers Number"
            type="number"
            min={1}
            step={1}
            className={styles.Input}
            value={data.youtubeFollowersCount || ''}
            onChange={(e) => handlePositiveNumberChange(e, 'youtubeFollowersCount')}
            onKeyDown={handleNumberKeyDown}
          />
        </FormField>
      </div>
      <div className={styles.SwitchAndInput} style={errors?.tiktokFollowersCount ? { alignItems: 'center' } : { alignItems: 'flex-end' }}>
        <Switch.Root
          className={styles.Root}
          id="tiktok"
          checked={data.tiktokFollowersRequired || false}
          onCheckedChange={(checked) => updateData({ tiktokFollowersRequired: checked })}
        >
          <Switch.Thumb className={styles.Thumb} />
        </Switch.Root>

        <FormField label="TikTok" error={errors?.tiktokFollowersCount}>
          <input
            disabled={!data.tiktokFollowersRequired}
            placeholder="Type Min Followers Number"
            type="number"
            min={1}
            step={1}
            className={styles.Input}
            value={data.tiktokFollowersCount || ''}
            onChange={(e) => handlePositiveNumberChange(e, 'tiktokFollowersCount')}
            onKeyDown={handleNumberKeyDown}
          />
        </FormField>
      </div>
      {/* Preapproval of influencer */}

      <div className={styles.CheckBoxWrapper}>
        <input
          className={styles.CheckBoxInput}
          id="preapproveInfluencers"
          type="checkbox"
          checked={data.preapproveInfluencers || false}
          onChange={(e) => updateData({ preapproveInfluencers: e.target.checked })}
        />
        <label className={styles.Label} htmlFor="preapproveInfluencers">
          Preapprove influencers
        </label>
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button type="button" className={styles.IconButton}>
                <InfoCircleIcon color="#566676" />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.Content} sideOffset={5} side="right">
                Requires influencer to email Support for preapproval; funds transferred manually.
                <Tooltip.Arrow className={styles.Arrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>
    </div>
  )
}
